import { useState, useEffect } from "react";
import AppLayout from "../layout/AppLayout";
import { Spinner } from "react-bootstrap";
import { positions, useAlert } from "react-alert";
import Cookies from "js-cookie";
import regenerateToken from "../regenerateToken";
import Loader from "../loader/loader";
import './patientDetailsConfig.css';
import { FaCheckCircle, FaTrashAlt, FaPlus, FaListAlt, FaCog } from 'react-icons/fa';
import axios from 'axios';
import { buildRequestConfig } from '../../utils/apiHelper';
import { isAdmin as checkAdmin } from '../../utils/authUtils';

const patientHeaders = {
  type1: [
    { title: "Name", value: "Name" },
    { title: "Accession No", value: "Accession No" },
    { title: "Basic Info", value: "Basic Info" },
    { title: "Date of Test", value: "Date of Test" },
  ],

  type2: [
    { title: "Name", value: "Name" },
    { title: "Age/Gender", value: "Age/Gender" },
    { title: "Centre", value: "Centre" },
    { title: "Lab ID", value: "Lab ID" },
    { title: "Referred by", value: "Referred by" },
    { title: "Collection Date", value: "Collected Date" },
    { title: "Receiving Date", value: "Received Date" },
    { title: "Reporting Date", value: "Reported Date" },
  ],

  fieldOptions1: [
    "Name",
    "Accession No",
    "Basic Info",
    "Date of Test",
    "Collection Date",
    "Receiving Date",
    "Reporting Date",
    "Referred by",
    "Barcode No",
  ],
  fieldOptions2: [
    "Name",
    "Age/Gender",
    "Centre",
    "Lab ID",
    "Referred by",
    "Collection Date",
    "Receiving Date",
    "Reporting Date",
    "Barcode No",
  ],
};

const PatientDetailsConfig = () => {
  const [patientDetails1, setPatientDetails1] = useState(patientHeaders.type1);
  const [patientDetails2, setPatientDetails2] = useState(patientHeaders.type2);
  const [headerType, setHeaderType] = useState("type1");
  const [additionalField1, setAdditionalField1] = useState("");
  const [additionalField2, setAdditionalField2] = useState("");
  const [loader, setLoader] = useState(false);
  const [dataLoader, setDataLoader] = useState(false);
  const alert = useAlert();

  useEffect(() => {
    getPatientDetailsData();
  }, []);

  /**
   * Normalises the response from either axios or native fetch.
   * - axios response: data is already parsed JS object in response.data
   * - fetch response: must call response.json() to parse the body
   * This is needed because admin path uses axios, client path uses fetch.
   */
  const parseResponse = async (response) => {
    if (typeof response.json === 'function') {
      return await response.json(); // native fetch
    }
    return response.data; // axios — already parsed
  };

  const getStateConfiguration = async () => {

    // ── ADMIN PATH: use admin token (axios) + inject clientId if a client is selected
    if (checkAdmin()) {
      return await axios(buildRequestConfig({
        method: "GET",
        url: "https://ap94204xve.execute-api.ap-south-1.amazonaws.com/prod/config/patientDetails",
      }));
    }
    // ── CLIENT PATH (unchanged — uses native fetch) ────────────────────
    const idToken = Cookies.get("idToken");
    const url =
      "https://ap94204xve.execute-api.ap-south-1.amazonaws.com/prod/config/patientDetails";
    const options = {
      headers: {
        authorization: idToken,
      },
      method: "GET",
    };
    const existingStateConfigResponse = await fetch(url, options);
    return existingStateConfigResponse;
  };

  const updateExistingStateFromGet = (data) => {
    let statePatientData = [];
    if (data?.headerType) {
      setHeaderType(data.headerType);
    }
    if (data?.headerConfig && Object.keys(data.headerConfig).length) {
      for (let [key, value] of Object.entries(data.headerConfig)) {
        statePatientData.push({ title: key, value: value });
      }
    }
    if (data?.headerType === "type1") {
      setPatientDetails1(statePatientData);
    } else if (data?.headerType === "type2") {
      setPatientDetails2(statePatientData);
    }
  };

  const getPatientDetailsData = async () => {
    setDataLoader(true);
    try {
      const existingStateConfigResponse = await getStateConfiguration();

      const statusCode = existingStateConfigResponse.status;
      if (statusCode === 401) {
        // API Call is to be made for regenerating the ID Token.
        await regenerateToken();
        const existingStateConfigResponse = await getStateConfiguration();
        const data = await parseResponse(existingStateConfigResponse);
        if (data?.data?.patientDetailsData) {
          updateExistingStateFromGet(data.data.patientDetailsData);
        } else {
          alert.error(<span className="alertError">{data?.message}</span>, {
            positions: positions.TOP_CENTER,
          });
        }
      } else {
        const data = await parseResponse(existingStateConfigResponse);
        if (data?.data?.patientDetailsData) {
          updateExistingStateFromGet(data.data.patientDetailsData);
        }
      }
    } catch (error) {
      console.log("error", error.message);
      alert.error(
        <span className="alertError">
          Failed to load patient details data. Please try again
        </span>,
        {
          positions: positions.TOP_CENTER,
        }
      );
    } finally {
      setDataLoader(false);
    }
  };

  const modifySpecificData = (data, key, value) => {
    const modifiedResult = data.map((each) => {
      if (each.title === key) {
        each.value = value;
      }
      return each;
    });
    console.log("modifiedResult", modifiedResult);
    return modifiedResult;
  };

  const handleTitleChange = (key, value) => {
    switch (headerType) {
      case "type1":
        setPatientDetails1(modifySpecificData(patientDetails1, key, value));
        break;
      case "type2":
        setPatientDetails2(modifySpecificData(patientDetails2, key, value));
        break;
      default:
        break;
    }
  };

  const handleDelete = (event, title) => {
    event.preventDefault();
    if (headerType === "type1") {
      const filteredData1 = patientDetails1.filter(
        (each) => each.title !== title
      );
      setPatientDetails1(filteredData1);
    } else {
      const filteredData2 = patientDetails2.filter(
        (each) => each.title !== title
      );
      setPatientDetails2(filteredData2);
    }
  };

  const handleFiledsSelection = (value) => {
    if (headerType === "type1") {
      setAdditionalField1(value);
    } else {
      setAdditionalField2(value);
    }
  };

  const checkIsAvailable = (data, value) => {
    let index = data.findIndex((each) => each.title === value);
    console.log("index", index);
    return index != -1;
  };

  const handleAddField = (e) => {
    e.preventDefault();
    switch (headerType) {
      case "type1":
        if (additionalField1) {
          if (checkIsAvailable(patientDetails1, additionalField1)) {
            alert.info("Field already exists! Try adding a different field");
            return;
          }
          setPatientDetails1((prev) => [
            ...prev,
            { title: additionalField1, value: additionalField1 },
          ]);
        }
        break;
      case "type2":
        if (additionalField2) {
          if (checkIsAvailable(patientDetails2, additionalField2)) {
            alert.info("Field already exists! Try adding a different field");
            return;
          }
          setPatientDetails2((prev) => [
            ...prev,
            { title: additionalField2, value: additionalField2 },
          ]);
        }
        break;
      default:
        break;
    }
  };

  const formatPatientDetailsData = (data) => {
    let result = {};
    if (data.length) {
      data.forEach((each) => {
        result[each.title] = each.value;
      });
    }
    console.log("result", result);
    return result;
  };

  const postPatientDetailsData = async () => {
    let patientDetailsFinal =
      headerType === "type1" ? patientDetails1 : patientDetails2;
    let payload = {
      headerType: headerType,
      headerConfig: formatPatientDetailsData(patientDetailsFinal),
    };
    // ── ADMIN PATH: use admin token (axios) + inject clientId if a client is selected
    if (checkAdmin()) {
      return await axios(buildRequestConfig({
        method: "POST",
        url: "https://ap94204xve.execute-api.ap-south-1.amazonaws.com/prod/config/patientDetails",
        data: payload,
      }));
    }
    // ── CLIENT PATH (unchanged — uses native fetch) ────────────────────
    const idToken = Cookies.get("idToken");
    const apiUrl =
      "https://ap94204xve.execute-api.ap-south-1.amazonaws.com/prod/config/patientDetails";
    const options = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: idToken,
      },
      body: JSON.stringify(payload),
    };

    const response = await fetch(apiUrl, options);
    return response;
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (
      (headerType === "type1" && !patientDetails1.length) ||
      (headerType === "type2" && !patientDetails2.length)
    ) {
      alert.info("Patient details data cannot be empty");
      return;
    }
    setLoader(true);
    try {
      const response = await postPatientDetailsData();
      if (response.status === 401) {
        await regenerateToken();
        const response = await postPatientDetailsData();
        const data = await parseResponse(response);
        if (data.isSuccess) {
          alert.success(<span>Data updated successfully!</span>, {
            position: positions.TOP_CENTER,
          });
        }
      } else {
        const data = await parseResponse(response);
        console.log("post response", data);
        if (data.isSuccess) {
          alert.success(<span>Data updated successfully!</span>, {
            position: positions.TOP_CENTER,
          });
        }
      }
    } catch (e) {
      alert.error(
        <span className="alertError">
          Something went wrong. Please try again.
        </span>,
        {
          position: positions.TOP_CENTER,
        }
      );
    } finally {
      setLoader(false);
    }
  };

  const dynamicPatientDetails =
    headerType === "type1" ? patientDetails1 : patientDetails2;

  const dynamicFieldOptions =
    headerType === "type1"
      ? patientHeaders.fieldOptions1
      : patientHeaders.fieldOptions2;

  return (
    <AppLayout>
      {dataLoader ? (
        <Loader />
      ) : (
        <div className="pd-container">
          {/* Header */}
          <header className="pd-header">
            <div className="pd-title-group">
              <h1 className="pd-title">Patient Details Configuration</h1>
              <p className="pd-subtitle">Manage the fields displayed on your patient reports.</p>
            </div>
            <div className="pd-actions-top">
              <button
                className="pd-btn-primary"
                onClick={handleSubmit}
                disabled={loader}
              >
                {loader ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  <>
                    <FaCheckCircle /> Confirm Changes
                  </>
                )}
              </button>
            </div>
          </header>

          <div className="pd-bento-grid">
            {/* Main Content: Active Fields List */}
            <div className="pd-card">
              <div className="pd-card-header">
                <span className="d-flex align-items-center gap-2">
                  <FaListAlt style={{ color: '#3b82f6' }} />
                  <h3 className="pd-card-title">Active Fields List</h3>
                </span>
                <span style={{ fontSize: '0.85rem', color: '#64748b' }}>
                  {dynamicPatientDetails.length} fields enabled
                </span>
              </div>
              <div className="pd-card-body">
                {dynamicPatientDetails.length === 0 ? (
                  <div className="text-center py-5 text-muted">
                    No fields selected. Please add fields from the panel on the right.
                  </div>
                ) : (
                  <div className="pd-field-list">
                    {dynamicPatientDetails.map((each) => (
                      <div key={each.title} className="pd-field-row">
                        <div className="pd-field-label">
                          {each.title}
                        </div>
                        <div className="pd-input-wrapper">
                          <input
                            className="pd-input"
                            value={each.value}
                            onChange={(e) => handleTitleChange(each.title, e.target.value)}
                            placeholder="Display Name"
                          />
                        </div>
                        <button
                          className="pd-delete-btn"
                          onClick={(e) => handleDelete(e, each.title)}
                          title="Remove Field"
                        >
                          <FaTrashAlt size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Right Sidebar: Settings & Tools */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>

              {/* 1. Configuration Mode */}
              <div className="pd-card">
                <div className="pd-card-header">
                  <span className="d-flex align-items-center gap-2">
                    <FaCog style={{ color: '#64748b' }} />
                    <h3 className="pd-card-title">Configuration Mode</h3>
                  </span>
                </div>
                <div className="pd-card-body">
                  <div className="pd-type-selector">
                    <div
                      className={`pd-type-option ${headerType === 'type1' ? 'active' : ''}`}
                      onClick={() => setHeaderType("type1")}
                      role="button"
                      tabIndex={0}
                      aria-pressed={headerType === 'type1'}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setHeaderType("type1");
                        }
                      }}
                    >
                      <div className="pd-radio-custom"></div>
                      <div>
                        <div style={{ fontWeight: '600', fontSize: '0.95rem' }}>Type 1 Header</div>
                        <div style={{ fontSize: '0.8rem', color: '#64748b' }}>Standard Layout</div>
                      </div>
                    </div>
                    <div
                      className={`pd-type-option ${headerType === 'type2' ? 'active' : ''}`}
                      onClick={() => setHeaderType("type2")}
                      role="button"
                      tabIndex={0}
                      aria-pressed={headerType === 'type2'}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setHeaderType("type2");
                        }
                      }}
                    >
                      <div className="pd-radio-custom"></div>
                      <div>
                        <div style={{ fontWeight: '600', fontSize: '0.95rem' }}>Type 2 Header</div>
                        <div style={{ fontSize: '0.8rem', color: '#64748b' }}>Extended Layout</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* 2. Add New Field */}
              <div className="pd-card">
                <div className="pd-card-header">
                  <span className="d-flex align-items-center gap-2">
                    <FaPlus style={{ color: '#10b981' }} />
                    <h3 className="pd-card-title">Add New Field</h3>
                  </span>
                </div>
                <div className="pd-card-body">
                  <div className="pd-add-field-group">
                    <select
                      className="pd-select"
                      onChange={(e) => handleFiledsSelection(e.target.value)}
                      value={headerType === 'type1' ? additionalField1 : additionalField2}
                    >
                      <option value="">Select a field</option>
                      {dynamicFieldOptions.map((each) => (
                        <option key={each} value={each}>
                          {each}
                        </option>
                      ))}
                    </select>
                    <button className="pd-btn-add" onClick={handleAddField}>
                      <FaPlus size={12} style={{ marginRight: '6px' }} /> Add to List
                    </button>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
};

export default PatientDetailsConfig;
